import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { services } from '../data/services';
import { Button } from '../components/ui/Button';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './ServiceDetail.css';

/**
 * ServiceDetail page showing a single service with full description and image gallery
 * Uses dynamic route parameter to load service data
 */
export const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const service = services.find(s => s.id === id);
  useDocumentTitle(service?.title);
  const imageContainerRef = useScrollReveal({ threshold: 0.1 });
  const relatedRef = useScrollReveal({ threshold: 0.1 });

  const relatedServices = useMemo(() => {
    return services.filter(s => s.id !== id).slice(0, 3);
  }, [id]);

  const getImage = (path: string) => {
    try {
      return require(`../assets/images/${path}`);
    } catch {
      return '';
    }
  };

  if (!service) {
    return (
      <div className="service-detail-page">
        <div className="service-detail__error">
          <div className="service-detail__error-content">
            <h1 className="service-detail__error-title">We couldn't find that service</h1>
            <p className="service-detail__error-message">
              This service is no longer available at this address.
            </p>
            <Button asLink="/services" variant="primary">
              Explore Services
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-detail-page page-enter">
      {/* Dark Hero Header — compact, consistent with other pages */}
      <section className="service-detail__hero">
        <div className="service-detail__hero-content">
          <nav className="service-detail__breadcrumb">
            <Link to="/services" className="service-detail__breadcrumb-link">
              Services
            </Link>
            <span className="service-detail__breadcrumb-separator">/</span>
            <span className="service-detail__breadcrumb-current">{service.title}</span>
          </nav>
          <h1 className="service-detail__title">{service.title}</h1>
        </div>
      </section>

      {/* Main Content — two-column on desktop: text left, image right */}
      <article className="service-detail__content">
        <div className="service-detail__container">
          <div className="service-detail__body">
            {/* Left Column: Description */}
            <div className="service-detail__text-section">
              <div className="service-detail__intro">
                <p className="service-detail__intro-text">{service.description}</p>
              </div>

              <div className="service-detail__full-description">
                <p>{service.longDescription}</p>
              </div>

              <div className="service-detail__cta">
                <p className="service-detail__cta-text">Have a project in mind?</p>
                <Button asLink="/contact" variant="primary" size="lg">
                  Start a Conversation
                </Button>
              </div>
            </div>

            {/* Right Column: First image, sticky while text scrolls */}
            {service.gallery && service.gallery.length > 0 && (
              <div className="service-detail__image-col">
                <img
                  src={getImage(service.gallery[0])}
                  alt={`${service.title} project example`}
                  className="service-detail__image-col-img"
                  loading="eager"
                  decoding="async"
                />
              </div>
            )}
          </div>
        </div>
      </article>

      {/* Remaining Gallery Images — skip first (already shown above) */}
      {service.gallery && service.gallery.length > 1 && (
        <section className="service-detail__gallery">
          <div className="service-detail__container">
            <h2 className="service-detail__gallery-title">Selected Work</h2>
            <div
              className="service-detail__gallery-grid"
              ref={imageContainerRef}
            >
              {service.gallery.slice(1).map((imagePath, index) => (
                <div
                  key={`${service.id}-${index}`}
                  className="service-detail__gallery-item"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <img
                    src={getImage(imagePath)}
                    alt={`${service.title} project example ${index + 2}`}
                    className="service-detail__gallery-image"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Services */}
      {relatedServices.length > 0 && (
        <section className="service-detail__related">
          <div className="service-detail__container">
            <h2 className="service-detail__related-title">More Services</h2>
            <div className="service-detail__related-grid reveal" ref={relatedRef}>
              {relatedServices.map((related) => (
                <Link
                  key={related.id}
                  to={`/services/${related.id}`}
                  className="service-detail__related-card"
                >
                  <div className="service-detail__related-image-wrapper">
                    <img
                      src={getImage(related.image)}
                      alt={related.title}
                      className="service-detail__related-image"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h3 className="service-detail__related-name">{related.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer Navigation */}
      <footer className="service-detail__footer">
        <div className="service-detail__container">
          <Button asLink="/services" variant="secondary">
            &#8592; Back to Services
          </Button>
        </div>
      </footer>
    </div>
  );
};

ServiceDetail.displayName = 'ServiceDetail';
